import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Download, Loader2, ShieldCheck, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "risorice_admin_token";

const TABS = [
  { key: "brochure", label: "Brochure", cols: ["created_at", "name", "email", "phone", "lang"] },
  { key: "franchise", label: "Franchising", cols: ["created_at", "name", "email", "phone", "city", "budget"] },
  { key: "business_plan", label: "Business Plan", cols: ["created_at", "buyer_name", "buyer_email", "buyer_phone", "buyer_city", "payment_status"] },
  { key: "contacts", label: "Contatti", cols: ["created_at", "name", "email", "message"] },
];

const COL_LABELS = {
  created_at: "Data", name: "Nome", email: "Email", phone: "Telefono", lang: "Lingua",
  city: "Città", budget: "Budget", buyer_name: "Nome", buyer_email: "Email",
  buyer_phone: "Telefono", buyer_city: "Città", payment_status: "Pagamento", message: "Messaggio",
};

const fmt = (v, col) => {
  if (v == null || v === "") return "—";
  if (col === "created_at") { try { return new Date(v).toLocaleString("it-IT"); } catch { return v; } }
  return String(v);
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email: email.trim(), password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-5" data-testid="admin-login">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-2xl">
        <div className="flex items-center gap-2 text-ink">
          <ShieldCheck className="text-tricolor-green" size={22} />
          <span className="font-display font-extrabold text-xl">RISORICE<span className="text-tricolor-green">.</span> Admin</span>
        </div>
        <p className="mt-2 text-sm text-muted">Area riservata — accedi per gestire i contatti.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="admin-login-email" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-ink mb-1.5">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-4 py-2.5 focus:outline-none focus:border-tricolor-green" data-testid="admin-login-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Accedi"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("brochure");
  const [loading, setLoading] = useState(true);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, auth);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async (kind) => {
    try {
      const res = await axios.get(`${API}/admin/leads/export?kind=${kind}`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `risorice-${kind}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Export non riuscito"); }
  };

  const current = TABS.find((x) => x.key === tab);
  const rows = data?.[tab] || [];

  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-ink text-white">
        <div className="mx-auto max-w-[1280px] px-5 md:px-8 h-16 flex items-center justify-between">
          <span className="font-display font-extrabold text-lg">RISORICE<span className="text-tricolor-green">.</span> Admin</span>
          <div className="flex items-center gap-3">
            <button onClick={load} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="admin-refresh"><RefreshCw size={16} /> Aggiorna</button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="admin-logout"><LogOut size={16} /> Esci</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1280px] px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {TABS.map((x) => (
            <button key={x.key} onClick={() => setTab(x.key)}
              className={`rounded-2xl border p-5 text-left transition-colors ${tab === x.key ? "border-tricolor-green bg-white" : "border-line bg-white/60 hover:bg-white"}`}
              data-testid={`admin-tab-${x.key}`}>
              <div className="font-display font-extrabold text-3xl text-ink">{data?.counts?.[x.key] ?? "—"}</div>
              <div className="text-xs text-muted mt-1 uppercase tracking-wide">{x.label}</div>
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-ink">{current.label} · {rows.length}</h2>
          <button onClick={() => exportCsv(tab)} className="inline-flex items-center gap-2 rounded-full bg-ink text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity" data-testid="admin-export-btn">
            <Download size={16} /> Esporta CSV
          </button>
        </div>

        <div className="rounded-2xl border border-line bg-white overflow-x-auto">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted"><Loader2 className="animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted" data-testid="admin-empty">Nessun dato.</div>
          ) : (
            <table className="w-full text-sm min-w-[640px]" data-testid="admin-table">
              <thead>
                <tr className="text-left text-muted border-b border-line bg-surface">
                  {current.cols.map((col) => <th key={col} className="py-3 px-4 font-semibold">{COL_LABELS[col] || col}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-line/60 hover:bg-surface/60" data-testid={`admin-row-${i}`}>
                    {current.cols.map((col) => (
                      <td key={col} className="py-3 px-4 text-ink/80 max-w-[260px] truncate">{fmt(r[col], col)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); setChecking(false); });
  }, [token]);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-ink text-white"><Loader2 className="animate-spin" /></div>;
  }
  if (!token) return <LoginForm onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
